import * as React from 'react';
import tw from 'twin.macro';
import { useTranslation } from 'next-i18next';

import { GoogleAutoCompleteInput, GooglePlacePrediction } from '@restworld/ui-ds';
import { getLocationAddressFields } from '@restworld/utils-common';

import {
  ManualEntryPromptText,
  ManualEntryPromptLink,
  ManualEntryPromptWrapper,
  ManualEntryPromptHint,
} from './manual-venue-entry.styled';

export type ManualAddressResult = {
  addressString: string;
  lat?: number;
  lon?: number;
  locality?: string;
  country?: string;
  postal_code?: string;
  administrative_area_level_1?: string;
  administrative_area_level_2?: string;
};

/**
 * "Your business not on Google Maps? Click here to enter manually" prompt.
 * Renders only when `visible` is true.
 */
export const ManualEntryPrompt: React.FC<{
  visible: boolean;
  onClick: () => void;
}> = ({ visible, onClick }) => {
  const { t } = useTranslation('restaurant-onboarding');

  if (!visible) return null;

  return (
    <ManualEntryPromptWrapper>
      <ManualEntryPromptText>
        {t('your_business_not_on_google_maps')}{' '}
        <ManualEntryPromptLink onClick={onClick}>{t('click_here_to_enter_manually')}</ManualEntryPromptLink>
      </ManualEntryPromptText>
      <ManualEntryPromptHint>{t('manual_entry_what_happens')}</ManualEntryPromptHint>
    </ManualEntryPromptWrapper>
  );
};

/**
 * Address autocomplete field with `['address']` filter that geocodes
 * the selected suggestion via `getLocationAddressFields`.
 *
 * Calls `onAddressChange` on every keystroke and `onAddressSelect`
 * with the geocoded result when a suggestion is picked.
 *
 * `initialValue` pre-fills the input (used in flows where an address
 * already exists, e.g. editing the address that the form was reset with
 * from a previous GMaps selection).
 */
export const ManualAddressInput: React.FC<{
  formId: string;
  placeholder?: string;
  initialValue?: string;
  onAddressChange: (value: string) => void;
  onAddressSelect: (result: ManualAddressResult) => void;
}> = ({ formId, placeholder = 'es. Via Roma 1, 00100, Roma', initialValue, onAddressChange, onAddressSelect }) => {
  const handleSuggestionSelect = React.useCallback(
    async (suggestion: GooglePlacePrediction) => {
      const { main_text, secondary_text } = suggestion.structured_formatting;
      const addressString = `${main_text}${secondary_text ? `, ${secondary_text}` : ''}`;
      onAddressChange(addressString);
      try {
        const addressFields = await getLocationAddressFields(addressString);
        // Forward the full geocoded bundle so manual-entry restaurants aren't
        // saved with NULL locality / postal_code / administrative_area_*.
        onAddressSelect({
          addressString,
          lat: addressFields.address_lat,
          lon: addressFields.address_lon,
          locality: addressFields.locality,
          country: addressFields.country,
          postal_code: addressFields.postal_code,
          administrative_area_level_1: addressFields.administrative_area_level_1,
          administrative_area_level_2: addressFields.administrative_area_level_2,
        });
      } catch {
        // Geocoding failed — pass address string without coordinates
        onAddressSelect({ addressString });
      }
    },
    [onAddressChange, onAddressSelect]
  );

  return (
    <GoogleAutoCompleteInput
      placesFilterTypes={['address']}
      formId={formId}
      key={formId}
      placeholder={placeholder}
      formValue={initialValue}
      onSuggestionSelect={handleSuggestionSelect}
      inputProps={{
        bordercolor: 'neutral-90' as const,
        type: 'text',
        id: formId,
        isFullwidth: true,
        wrapperStyle: tw`w-full`,
        onChange: onAddressChange,
      }}
      dropdownItemProps={{
        itemRenderVariant: 'without_break_line' as const,
      }}
      mainWrapperStyle={tw`w-full`}
      withFloatingUI={true}
    />
  );
};
